import { JFormik } from '@centerpiece/components';
import * as Yup from 'yup';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  useGetProjectTypesQuery,
  useDeleteProjectTypeMutation,
  useUpdateProjectTypeMutation,
  useCreateProjectTypeMutation,
  useCreateMarketSegmentMutation,
  useDeleteMarketSegmentMutation,
  useGetMarketSegmentsQuery,
  useUpdateMarketSegmentMutation,
  useCreateContractTypeMutation,
  useDeleteContractTypeMutation,
  useGetContractTypesQuery,
  useUpdateContractTypeMutation,
  useCreateBillingTypeMutation,
  useCreatePaymentTypeMutation,
  useDeleteBillingTypeMutation,
  useDeletePaymentTypeMutation,
  useGetBillingTypesQuery,
  useGetPaymentTypesQuery,
  useUpdateBillingTypeMutation,
  useUpdatePaymentTypeMutation,
} from 'store/api';
import { getIn } from 'formik';

export const ProjectTypesTab = () => {
  const {
    data: types = [],
    isLoading,
    refetch: refetchTypes,
  } = useGetProjectTypesQuery({});
  const [createProjectType] = useCreateProjectTypeMutation();
  const [editProjectType] = useUpdateProjectTypeMutation();
  const [deleteProjectType] = useDeleteProjectTypeMutation();

  const {
    data: billing_types = [],
    isLoading: billingTypesLoading,
    refetch: refetchBillingTypes,
  } = useGetBillingTypesQuery({});
  const [createBillingType] = useCreateBillingTypeMutation();
  const [editBillingType] = useUpdateBillingTypeMutation();
  const [deleteBillingType] = useDeleteBillingTypeMutation();

  const {
    data: payment_types = [],
    isLoading: paymentTypesLoading,
    refetch: refetchPaymentTypes,
  } = useGetPaymentTypesQuery({});
  const [createPaymentType] = useCreatePaymentTypeMutation();
  const [editPaymentType] = useUpdatePaymentTypeMutation();
  const [deletePaymentType] = useDeletePaymentTypeMutation();

  const {
    data: contract_types = [],
    isLoading: contractTypesLoading,
    refetch: refetchContractTypes,
  } = useGetContractTypesQuery({});
  const [createContractType] = useCreateContractTypeMutation();
  const [editContractType] = useUpdateContractTypeMutation();
  const [deleteContractType] = useDeleteContractTypeMutation();

  const { data: market_segments = [], isLoading: isMSLoading } =
    useGetMarketSegmentsQuery({});
  const [createMarketSegment] = useCreateMarketSegmentMutation();
  const [editMarketSegment] = useUpdateMarketSegmentMutation();
  const [deleteMarketSegment] = useDeleteMarketSegmentMutation();
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <JFormik
      initialValues={() => ({
        types: types,
        contract_types: contract_types,
        payment_types: payment_types,
        billing_types: billing_types,
      })}
      enableReinitialize
      handleSubmit={async (values, formik) => {}}
      loading={isLoading || isMSLoading}
      validationSchema={Yup.object({})}
      disabled={true}
      form={[
        {
          name: 'custom-fields',
          itemType: 'typography',
          jItemProps: {
            variant: 'h6',
            sx: { mb: '0 !important', pb: '0 !important' },
          },
          label: <span>Project Types</span>,
        },
        {
          name: 'types',
          itemType: 'fieldarray',
          label: 'Project Types',
          jItemProps: {
            addLabel: 'Add Project Type',
            onCreate: async (values: any) => {
              await createProjectType(values);
            },
            onEdit: async (values: any) => {
              await editProjectType({
                ...values,
                estimated_win_rate:
                  values.estimated_win_rate !== ''
                    ? values.estimated_win_rate
                    : undefined,
              });
            },
            disableDelete: (values: any, index: number) => {
              const rowValue = values?.types?.[index];
              return rowValue?.subscription === undefined && rowValue?.id > 0;
            },
            onDelete: async (id: number) => {
              await deleteProjectType(id);
            },
            accordion: (values: any, row: any, index: number, name: string) => {
              if (row.id > 0) {
                return {
                  name: 'market_segments',
                  itemType: 'fieldarray',
                  label: 'Market Segments',
                  gridProps: {
                    sx: { paddingTop: '10px !important' },
                  },
                  jItemProps: {
                    details_label: 'Market Segments',
                    onCreate: async (
                      values: any,
                      allValues: any,
                      jItem: string,
                      index: any
                    ) => {
                      await createMarketSegment({
                        ...values,
                        project_type: getIn(
                          allValues,
                          jItem.replace('market_segments', 'id')
                        ),
                      });
                      await refetchTypes();
                    },
                    onEdit: async (values: any) => {
                      await editMarketSegment({
                        ...values,
                      });
                      await refetchTypes();
                    },
                    onDelete: async (id: number) => {
                      await deleteMarketSegment(id);
                      await refetchTypes();
                    },
                  },
                  array: [
                    {
                      name: 'name',
                      label: 'Market Segment *',
                      itemType: 'textfield',
                      // gridProps: { xs: 4 },
                    },
                  ],
                };
              }
            },
          },
          array: [
            {
              name: 'name',
              label: 'Type *',
              itemType: 'textfield',
              jItemProps: {
                // width: '65%',
                // sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                disabledFunc: (values: any) => {
                  return values?.subscription === undefined && values?.id > 0;
                },
              },
              gridProps: { xs: 4 },
            },
            {
              name: 'estimated_win_rate',
              label: 'Estimated Win Rate',
              itemType: 'textfield',
              jItemProps: { type: 'number' },
              gridProps: { xs: 3 },
            },
            {
              name: 'win_loss',
              label: 'Collect Win/Loss Data',
              itemType: 'checkbox',
              // jItemProps: {
              //   tooltip:
              //     "The project is considered 'Completed' with this outcome.",
              // },
              gridProps: { xs: 3 },
            },
          ],

          device: downSm ? 'mobile' : 'desktop',
        },
        // {
        //   name: 'custom-fields',
        //   itemType: 'typography',
        //   jItemProps: {
        //     variant: 'h6',
        //     sx: { mb: '0 !important', pb: '0 !important' },
        //   },
        //   label: <span>Contract Types</span>,
        // },
        {
          name: 'contract_types',
          itemType: 'table_array',
          label: 'Contract Types',
          tableProps: {
            onCreate: (values: any) =>
              createContractType({ name: values.name }),
            onEdit: (values: any) => editContractType(values),
            onDelete: (id: number) => deleteContractType(id),
            readOnly: (values: any) =>
              values?.subscription === undefined && values?.id > 0,
            // headers: [
            //   {
            //     name: 'name',
            //     label: 'Stakeholder *',
            //     itemType: 'textfield',
            //     jItemProps: {
            //       width: '100%',
            //       touched: true,
            //       sx: {
            //         boxSizing: 'border-box',
            //         px: { xs: 0.5 },
            //         minWidth: 180,
            //       },
            //     },
            //     gridProps: { xs: 12 },
            //   },
            //   // {
            //   //   name: 'type',
            //   //   label: "Type *",
            //   //   itemType: 'autocomplete',
            //   //   jItemProps: { width: '30%', touched: true, sx: { boxSizing: 'border-box', px: { xs: 0.5 }, minWidth: 160 } },
            //   //   gridProps: { xs: 12 },
            //   // },
            // ],
            columns: [
              {
                name: 'name',
                label: 'Type *',
                itemType: 'textfield',
                jItemProps: {
                  width: '70%',
                  touched: true,
                  sx: {
                    boxSizing: 'border-box',
                    px: { xs: 0.5 },
                    minWidth: 180,
                  },
                },
                gridProps: { xs: 12 },
              },
              // {
              //   name: 'type',
              //   label: "Type *",
              //   itemType: 'autocomplete',
              //   jItemProps: { width: '30%', touched: true, sx: { boxSizing: 'border-box', px: { xs: 0.5 }, minWidth: 160 } },
              //   options: ['Internal', 'External'],
              //   gridProps: { xs: 12 },
              // },
            ],
          },

          device: downSm ? 'mobile' : 'desktop',
        },
        {
          name: 'billing_types',
          itemType: 'table_array',
          label: 'Billing Types',
          tableProps: {
            onCreate: (values: any) => createBillingType({ name: values.name }),
            onEdit: (values: any) => editBillingType(values),
            onDelete: (id: number) => deleteBillingType(id),
            readOnly: (values: any) =>
              values?.subscription === undefined && values?.id > 0,
            columns: [
              {
                name: 'name',
                label: 'Type *',
                itemType: 'textfield',
                jItemProps: {
                  width: '70%',
                  touched: true,
                  sx: {
                    boxSizing: 'border-box',
                    px: { xs: 0.5 },
                    minWidth: 180,
                  },
                },
                gridProps: { xs: 12 },
              },
            ],
          },
          device: downSm ? 'mobile' : 'desktop',
        },
        {
          name: 'payment_types',
          itemType: 'table_array',
          label: 'Payment Types',
          tableProps: {
            onCreate: (values: any) => createPaymentType({ name: values.name }),
            onEdit: (values: any) => editPaymentType(values),
            onDelete: (id: number) => deletePaymentType(id),
            readOnly: (values: any) =>
              values?.subscription === undefined && values?.id > 0,
            columns: [
              {
                name: 'name',
                label: 'Type *',
                itemType: 'textfield',
                jItemProps: {
                  width: '70%',
                  touched: true,
                  sx: {
                    boxSizing: 'border-box',
                    px: { xs: 0.5 },
                    minWidth: 180,
                  },
                },
                gridProps: { xs: 12 },
              },
            ],
          },
          device: downSm ? 'mobile' : 'desktop',
        },
      ]}
    />
  );
};
